import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import { BaseApiService, CardsApiService, filePath, geteCardTypeByClassName, SwalService } from '@atmi/core';
import { CollectionOrderSettings, SettingsApiService } from '@atmi/core/services/settings-api.service';
import { CardOA, CardOAJoined } from '@atmi/models/cards/card-oa';
import {
  CollectionExportDialogComponent
} from '@sim/collections/dialogs/collection-export-dialog/collection-export-dialog.component';
import {
  defaultMeasurePrint
} from '@sim/collections/dialogs/collection-export-dialog/constants';
import { Collection, CollectionClassName, CollectionItem } from '@sim/collections/models/collection';
import { CollectionItemApiService } from '@sim/collections/services';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { getRouteFromClassName } from '@sim/audit/utils';
import { Base } from "@atmi/core/models";
import { ClassName, RoleService } from '@sim/role';

export interface ItemCollectionObject {
  position: number;
  id: string;
  img: string;
  titolo: string;
  materiale: string;
  misure: string;
  peso: string;
  anno: string;
  className: CollectionClassName;
  base: string;
  misureBase: string;
  materialeBase: string;
}

const parseTitleCard = (card: ExtCardOa): string => {
  const SGT = card.SGT;
  //if (SGT.SGTT && SGT.SGTI) return `${SGT.SGTT} - ${SGT.SGTI}`;
  if (SGT.SGTT) return SGT.SGTT;
  else if (SGT.SGTI) return SGT.SGTI;
  return '';
}

const parseMateriale = (mat: any[]) => {
  return mat.map(m => {
    if (typeof m === 'string') return m;
    if (m && typeof m === 'object') return Object.values(m).filter(Boolean).join('-');
    return '';
  }).filter(Boolean).join(' ')
}
export type ExtCardOa = CardOA & { _base: Base | null };

@Component({
  selector: 'app-collection-elements',
  templateUrl: './collection-elements.component.html',
  styleUrls: ['./collection-elements.component.scss']
})
export class CollectionElementsComponent implements OnInit, OnDestroy {

  @Input() set collection(collection: Collection | null) {
    if (collection) {
      this.collection$.next(collection);
    }
  }

  get collection(): Collection | null {
    return this.collection$.value;
  }

  /*@TODO passare l'ordinamento al dialog di stampa come prop oppure prenderlo dal servizio*/
  collection$: BehaviorSubject<Collection | null> = new BehaviorSubject<Collection | null>(null);

  @ViewChild('table', { static: true }) table!: MatTable<ItemCollectionObject>;

  readonly allDisplayedColumns: Array<string> = [
    'position',
    'img',
    'titolo',
    'materiale',
    'misure',
    'peso',
    'anno',
    'base',
    'misure-base',
    'materiale-base',
    'actions',
  ];

  displayedColumns: Array<string> = [...this.allDisplayedColumns];
  dataSource: ItemCollectionObject[] = [];
  dragDisabled = true;

  loading = false;
  collectionOrderSettings: CollectionOrderSettings = {};

  hasCapabilityToDelete = false;

  showSaveOrderButton = true;

  isCompactActions = false;

  private readonly destroy$ = new Subject<void>();

  constructor(private readonly collectionItemApi: CollectionItemApiService,
    private readonly cardApiService: CardsApiService,
    private readonly settingsApiService: SettingsApiService,
    public dialog: MatDialog,
    @Inject(filePath) readonly fileUrl: string,
    protected readonly router: Router,
    private readonly swal: SwalService,
    private readonly baseApi: BaseApiService,
    protected readonly translate: TranslateService,
    private readonly roleService: RoleService,
    private readonly breakpointObserver: BreakpointObserver) {
    
    // Debug logging
    console.log('CollectionElementsComponent - Current role:', this.roleService.role);
    console.log('CollectionElementsComponent - Role service:', this.roleService);
    
    this.hasCapabilityToDelete = (['BeneOA', 'BeneF', 'BeneMI', 'BeneD', 'BeneS'] as ClassName[]).some(
      (className) => {
        const hasDelete = this.roleService.hasCapability(className, ['delete']);
        console.log(`CollectionElementsComponent - ${className} has delete:`, hasDelete);
        return hasDelete;
      }
    );
    
    console.log('CollectionElementsComponent - hasCapabilityToDelete:', this.hasCapabilityToDelete);
  }

  ngOnInit(): void {
    this.collection$
      .pipe(takeUntil(this.destroy$))
      .subscribe((collection) => {
        if (collection?.id) {
          this.getCollectionItems(collection.id);
        }
      });

    this.settingsApiService.collectionOrderSettings$
      .pipe(takeUntil(this.destroy$))
      .subscribe((collectionOrderSettings) => {
        this.collectionOrderSettings = { ...collectionOrderSettings };
        if (this.dataSource.length) {
          this.sortByCollectionOrder();
        }
      });

    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium])
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        const isMediumOrBelow =
          state.breakpoints[Breakpoints.XSmall] ||
          state.breakpoints[Breakpoints.Small] ||
          state.breakpoints[Breakpoints.Medium];
        const hiddenColumns = ['anno', 'peso', 'materiale-base'];

        this.isCompactActions = isMediumOrBelow;

        this.displayedColumns = isMediumOrBelow
          ? this.allDisplayedColumns.filter((column) => !hiddenColumns.includes(column))
          : [...this.allDisplayedColumns];
      });


  }

  handleSaveOrder() {
    this.updateCollectionOrder();
  }

  sortByCollectionOrder() {
    const collectionId = this.collection$.value?.id || '';
    if (!collectionId) {
      return;
    }

    const currentCollectionOrderSettings = this.collectionOrderSettings[collectionId];
    if (!currentCollectionOrderSettings) {
      this.dataSource = this.dataSource.sort((a, b) => a.titolo?.localeCompare(b.titolo));
      this.innerUpdateCollectionOrder();
      return;
    }
    this.dataSource = this.dataSource.sort((a, b) => {
      const aIndex = currentCollectionOrderSettings.indexOf(a.id) || 0;
      const bIndex = currentCollectionOrderSettings.indexOf(b.id) || 0;
      return aIndex - bIndex;
    });

    this.table?.renderRows();

  }

  drop(event: CdkDragDrop<CollectionItem[]>) {
    // Return the drag container to disabled.
    this.dragDisabled = true;

    const previousIndex = this.dataSource.findIndex((d) => d === event.item.data);

    moveItemInArray(this.dataSource, previousIndex, event.currentIndex);
    this.table.renderRows();

    this.innerUpdateCollectionOrder();
  }

  innerUpdateCollectionOrder() {
    const collectionId = this.collection$.value?.id || '';
    if (!collectionId) {
      return;
    }
    this.collectionOrderSettings[collectionId] = this.dataSource.map(item => item.id);
  }

  updateCollectionOrder() {
    this.settingsApiService.updateCollectionOrderSettings(this.collectionOrderSettings);
  }

  async getCollectionItems(collectionId: string) {
    this.loading = true;
    if (!collectionId) {
      return;
    }
    try {
      const itemsRes = await firstValueFrom(this.collectionItemApi.index(collectionId).pipe(first()));
      const itemsObjects: ExtCardOa[] = await Promise.all(
        itemsRes.data.map((collectionItem) => {
          const cardType = geteCardTypeByClassName(collectionItem.className);
          return firstValueFrom(this.cardApiService.get(collectionItem.objectId, cardType).pipe(first()));
        })) as ExtCardOa[];
      for (let item of itemsObjects) {
        if (item.base) {
          const base = await firstValueFrom(this.baseApi.get(item.base).pipe(first()));
          item._base = base;
        } else item._base = null;
      }
      const parsedItemObjects = itemsObjects.map((card, idx) => {
        const className = itemsRes.data.find(item => item.objectId === card.id)?.className ?? 'BeneOA';
        const itemObject: ItemCollectionObject = {
          id: card.id || '',
          position: idx,
          img: card.previewUrl ?? '',
          titolo: parseTitleCard(card),
          materiale: card.MTC.length ? parseMateriale(card.MTC) : '',
          misure: defaultMeasurePrint(card as unknown as CardOAJoined),
          peso: card.MIS.MISG ? `kg ${card.MIS.MISG}` : '',
          anno: card.DT?.[0]?.DTS?.DTSI || '',
          className,
          base: card._base?.titolo ?? '',
          misureBase: `H:${card._base?.altezza ?? ''} L:${card._base?.larghezza ?? ''} P:${card._base?.profondita || ''}`,
          materialeBase: card._base?.materiale.length ? `${card._base?.materiale.join(' ')}` : ''

        };
        return itemObject;
      });

      this.dataSource = parsedItemObjects;

    } catch (err) {
      console.error(err);
    } finally {
      this.loading = false;
      this.sortByCollectionOrder();
    }
  }


  async exportSelected() {

    const exportDialog = this.dialog.open(CollectionExportDialogComponent, {
      width: '60vw',
      panelClass: 'export-dialog-container',
      disableClose: true,
      data: {
        collectionId: this.collection?.id,
        collectionOrderSettings: this.collectionOrderSettings
      }
    });
    exportDialog.afterClosed().subscribe((res: object) => {
      return res;
    });


  }

  handleEdit(element: ItemCollectionObject) {
    return this.router.navigate([getRouteFromClassName(element.className, element.id)]);
  }


  async handleDelete(element: ItemCollectionObject) {
    try {
      const res = await this.swal.showConfirm({
        text: this.translate.instant(`Sei sicuro di voler rimuovere ${element.titolo} dalla collection?`),
        title: this.translate.instant('Rimozione da collection'),
      });
      if (res) {
        await firstValueFrom(this.collectionItemApi.remove(this.collection?.id || '', element.id).pipe(first()));
        this.dataSource = this.dataSource.filter((item) => item.id !== element.id);
        this.innerUpdateCollectionOrder();
        this.updateCollectionOrder();
      }

    } catch (e: any) {
      console.error('error during remove from collection', e);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}


